import type { Messages } from '../messages.js'

/**
 * Armenian. `քայլ` for flips, the word an Armenian board game uses for a move.
 *
 * After a numeral Armenian keeps the noun singular — `2 բառ`, not `2 բառեր` — so both plural
 * forms carry the same string.
 *
 * Written in Eastern Armenian, which is what hy.wikipedia and hy.wiktionary are written in and
 * therefore what the word list is drawn from.
 */
export const hy: Messages = {
  tag: 'hy',

  readingDictionary: 'Բառարանը կարդացվում է…',
  noWordList: '«{language}»-ի համար բառացանկ չկա։ Ստեղծեք այն՝  pnpm dictionary build',
  emptyWordList: '«{language}»-ի բառացանկը դատարկ է։',

  flips: 'քայլեր',
  score: 'միավոր',
  words: 'բառեր',
  round: 'փուլ',
  ticksLeftLabel: 'Այս փուլում մնացած ժամանակը',
  typeAWord: 'մուտքագրեք բառ',
  tapPrompt: 'հպեք տառերին՝ ընտրելու կամ վերադարձնելու համար, ապա {action}',

  boardOfTiles: '{n} սալիկից տախտակ',
  faceDown: 'շրջված',
  wildCard: 'ջոկեր',
  wildKey: 'ցանկացած տառ',
  letterReplaced: '{from}-ը դարձավ {to}',
  letterSwap: 'ՏԱՌԵՐԻ ՓՈԽԱՆԱԿՈՒՄ',
  spentTile: 'օգտագործված սալիկ',
  hiddenWhilePaused: 'թաքցված է դադարի ընթացքում',
  letterInWord: '{letter}, բառի {position}-րդ տառը',

  completeWord: 'Ավարտել բառը',

  completeShort: 'Ավարտել',
  reset: 'Մաքրել',
  pause: 'Դադար',
  resume: 'Շարունակել',
  newGame: 'Նոր խաղ',
  paused: 'Դադարեցված',
  outOfFlips: 'Քայլերը սպառվեցին',
  finalResult: '{score}՝ {words}-ի համար, {rounds}-ում',
  playAgain: 'Խաղալ նորից',
  share: 'Կիսվել',
  shareCopied: 'Պատճենվեց։',
  shareSelect: 'Պատճենեք սա՝',

  lettersSelect: 'տառերն ընտրում են',
  keysWild: 'վերցվում է, երբ մուտքագրում եք տառ, որը ոչ մի սալիկ չի ցույց տալիս',
  clearsEvery: 'մաքրում է բոլոր ընտրված {letter}-երը',
  undoLastLetter: 'հետարկել վերջին տառը',
  noWordsYet: 'Դեռ բառեր չկան։',

  wordRejected: '{word}  {reason}',
  reasonDuplicate: 'արդեն գտնված է',
  reasonTooShort: 'շատ կարճ է',
  reasonNotAWord: 'բառ չէ',
  reasonAllFound: 'արդեն բոլորն ունեք',
  noSuchLetterUp: 'բացված {letter} չկա',
  nothingUp: 'ոչինչ բացված չէ',
  letterHidden: 'մի տառ թաքնվեց',
  shuffled: 'խառնվեց',
  shuffledAndBilled: 'խառնվեց, հաշվարկվեց {flips} չօգտագործված',
  tooFewLetters: 'Չափազանց քիչ տառ է մնացել',

  gameLanguage: 'լեզու',
  interfaceLanguage: 'միջերես',
  dictionarySize: '{full} բառից {common} առօրյա',
  filterLanguages: 'Փնտրել լեզու',
  noMatches: 'Համընկնում չկա',

  nerdMode: 'գիտակների ռեժիմ',
  rules: 'Կանոններ',
  difficulty: 'բարդություն',
  difficultyNames: { easy: 'հեշտ', medium: 'միջին', hard: 'դժվար', insane: 'խելագար' },
  pausedNoBoard: 'Այս խաղը վարկանիշային աղյուսակում չի հայտնվի։',
  about: 'Մասին',
  themeLabel: 'տեսք',
  themeNames: { traditional: 'ավանդական', light: 'լուսավոր', contrast: 'բարձր հակադրություն' },
  tiles: 'սալիկներ (N)',
  secondsPerTick: 'վայրկյան / տակտ',
  holdTicks: 'պահման տակտեր',
  minWord: 'ամենակարճ բառը',
  startingFlips: 'սկզբնական քայլեր',
  wildChance: 'ջոկերի հավանականություն',
  replaceChance: 'տառի փոխանակման հավանականություն',
  hideChance: 'տառի թաքնվելու հավանականություն',
  wordCompleteMode: 'բառն ավարտելիս',
  wordCompleteNames: { shuffle: 'խառնել', spend: 'ծախսել', keep: 'պահել' },
  flipEconomy: 'քայլերի վերադարձ',
  flipEconomyNames: {
    none: 'ոչ մեկը',
    perLetter: 'ըստ տառի',
    fibonacci: 'ֆիբոնաչի',
    overMinimum: 'նվազագույնից ավելի',
  },
  repeatedLetterKey: 'կրկնվող տառի ստեղն',
  keySchemeNames: { cycle: 'շրջան', advance: 'առաջ' },
  keySchemeHelp: {
    cycle:
      'Ա-ն վերցնում է հաջորդ չօգտագործված Ա-ն, և երբ բոլորը բառում են, մաքրում է դրանք։ ' +
      'Shift+Ա-ն նույնպես մաքրում է։',
    advance: 'Ա-ն վերցնում է հաջորդ չօգտագործված Ա-ն։ Shift+Ա-ն մաքրում է բառի բոլոր Ա-երը։',
  },

  whatThatMeans: 'Ինչ է դա նշանակում',
  factRound: 'փուլ',
  factWholeBoardUp: 'ամբողջ տախտակը բաց է',
  factRoundCosts: 'մեկ փուլն արժե',
  factFlipsBuy: 'սկզբնական քայլերը գնում են',
  factThisBoard: 'այս տախտակը',
  factBoardHadToAdmit: 'տախտակը պետք է թույլ տար',
  ticksAndSeconds: '{ticks}, {seconds}',
  wordsLongest: '{words}, ամենաերկարը՝ {longest}',
  wordsIncludingOneOf: '{words}, որոնցից մեկը՝ {ceiling}',
  scorelessRounds: '{rounds}՝ առանց միավորի',

  whatAWordPays: 'Ինչ է տալիս բառը',
  columnLetters: 'տառեր',
  columnCost: 'արժեք',
  columnPoints: 'միավոր',
  columnFlips: 'քայլեր',
  columnNet: 'տարբերություն',

  canonicalRules: '{difficulty} մակարդակի սովորական կանոնները։',
  customRules: 'Փոխված է կանխադրվածից։ Սեփական կանոններով միավորները վարկանիշում չեն մտնում։',
  applyAndStart: 'Կիրառել և սկսել նոր խաղ',
  changesNextGame: 'Փոփոխությունները կգործեն հաջորդ խաղից։',
  presets: 'Կանխադրվածներ՝',

  start: 'Սկսել',
  restart: 'Նորից',
  quit: 'Դուրս գալ',
  quitTitle: 'Ավարտե՞լ այս խաղը։',
  restartTitle: 'Նորի՞ց սկսել այս խաղը։',
  restartConfirm: 'Նորից',
  quitConfirm: 'Դուրս գալ',
  keepPlaying: 'Շարունակել խաղը',
  personalBest: 'Ձեր լավագույն խաղերը',
  newPersonalBest: 'Նոր անձնական ռեկորդ։',
  columnRank: '#',
  notRanked: 'Սեփական կանոններ, ուստի այս խաղը վարկանիշում չի մտնում։',
  notRankedPaused: 'Ժամանակը կանգնեց, ուստի այս խաղը վարկանիշում չի մտնում։',
  rankOfTotal: '{total}-ից {rank}',

  howToPlay: 'Ինչպես խաղալ',

  backToGame: 'Վերադառնալ խաղին',
  welcomeTitle: 'Բարի գալուստ Blinkered',
  tutorialSkip: 'Բաց թողնել',
  tutorialNext: 'Հաջորդ',
  tutorialBack: 'Հետ',
  tutorialStart: 'Սկսել խաղալ',
  tutorialHideAgain: 'Այլևս ցույց չտալ',
  tutorialProgress: '{total}-ից {n}',
  tutorialSkipTitle: 'Բաց թողնե՞լ ծանոթացումը։',
  tutGoalTitle: 'Blinkered-ը մրցավազք է',
  tutGoalBody:
    'Խաղը սկսում եք որոշակի քանակով քայլերով, և ամեն բացվող սալիկ մեկ քայլ արժե։ Երբ քայլերը սպառվեն, խաղն ավարտվում է։ Բառերը քայլեր են բերում, և որքան երկար է բառը, այնքան շատ քայլեր եք վաստակում։',
  tutSpentBody:
    'Բառում օգտագործված տառերը շրջվում են երեսով դեպի ներքև մինչև հաջորդ փուլը։ Միայն տառի փոխարինումն է փոխում, թե ինչ տառեր կան տախտակին։',
  tutChangeTitle: 'Տառերը կարող են փոխվել',
  tutSameLetters:
    'Նկատե՞լ եք բառ, որը չհասցրիք խաղալ։ Նույն տառերը վերադառնում են հաջորդ փուլում՝ խառնված նոր տեղերում։',
  tutPickLetters: 'Հպեք ցանկալի տառերին հերթականությամբ՝ բառ կազմելու համար։',
  tutMoreTurn:
    'Սալիկները շարունակում են բացվել, մինչ մտածում եք, այնպես որ ավելի լավ տառ դեռ կարող է գալ։',
  tutTapBack:
    'Վերցրե՞լ եք տառ, որը չէիք ուզում։ Կրկին հպեք, և այն կվերադառնա։ Ցանկացածը, ոչ միայն վերջինը։',
  tutComplete: 'Երբ բառը պատրաստ է, սեղմեք Ավարտել։',
  htBoardTitle: 'Տախտակը',
  htBoardBody:
    'Սալիկները բացվում են մեկ առ մեկ, պատահական հերթականությամբ։ Բացվածներից բառեր են կազմվում։',
  htWordsTitle: 'Բառերը',
  htWordsBody: 'Կազմեք բառ բացված սալիկներից՝ մուտքագրելով կամ սեղմելով տառերը հերթականությամբ։',
  htFlipsTitle: 'Քայլերը',
  htFlipsBody:
    'Ամեն բացվող սալիկ մեկ քայլ արժե։ Ավարտված բառը քայլեր է վերադարձնում, իսկ ավելի երկար բառերն ավելին։ Երբ քայլերը սպառվեն, խաղն ավարտվում է։',
  htRoundTitle: 'Փուլը',
  htRoundBody:
    'Երբ փուլի վերջին սալիկը բացվի, ամբողջ տախտակը մի պահ կանգ է առնում։ Ապա սալիկները շրջվում և խառնվում են, և սկսվում է նոր փուլ։',
  htLanguagesTitle: 'Լեզուները',
  htLanguagesBody:
    '{n} լեզու։ Յուրաքանչյուր տախտակ լուծվում է բառերով, որոնք մարդիկ իրոք գործածում են։ Հազվադեպ բառը նույնպես միավոր է տալիս, եթե բառարանը գիտի այն։',
  htKeysTitle: 'Ստեղնաշարը',
  htWildTitle: 'Ջոկերները',
  htWildBody:
    'Երբեմն տառի փոխարեն ջոկեր է հայտնվում։ Ջոկերը հաշվվում է որպես ցանկացած տառ, որը ճիշտ բառ է կազմում։ Արդեն գտնված բառը չի հաշվվում։',
  htHideTitle: 'Թաքնվող տառեր',
  htHideBody:
    'Ավելի դժվար մակարդակներում բացված տառը կարող է ինքնուրույն հետ շրջվել և վերադառնալ ավելի ուշ նույն փուլում։ Ժամաչափը դրա համար ստանում է լրացուցիչ նշիկ՝ կարմիրով։ Արդեն ընտրված տառերը հետ չեն շրջվում։',
  tutHideBody:
    'Ավելի դժվար մակարդակներում բացված տառը կարող է ինքնուրույն հետ շրջվել և վերադառնալ ավելի ուշ նույն փուլում։ Արդեն ընտրված տառերը հետ չեն շրջվում։',
  htSwapTitle: 'Փոխվող տառեր',
  htSwapBody:
    'Երբեմն փուլերի միջև մեկ տառը փոխարինվում է մյուսով։ Կտեսնեք, որն է հեռացել և որն է եկել։',
  htLevelsTitle: 'Մակարդակները',
  htLevelEasy:
    'Նույն տասներկու տառը ողջ խաղի ընթացքում, այնպես որ կարող եք սովորել դրանք և բառերի ցանկը գլխում պահել։ Սալիկները դանդաղ են բացվում, և ամբողջ տախտակը բավական երկար է երևում, որ ընտրությունն ավարտեք։',
  htLevelMedium:
    'Ժամանակ առ ժամանակ մի տառ փոխվում է, ուստի դժվարանում է հիշել հետո օգտագործելու համար պահված բառերը։ Ավելի քիչ ժամանակ նայելու և ավելի քիչ մտածելու։',
  htLevelHard:
    'Երեք տառանոց բառերն այլևս չեն հաշվվում, և տառը փոխվում է մոտավորապես ամեն երկրորդ փուլում։ Տախտակը հազիվ է երևում, արդեն խառնվում է։',
  htLevelInsane:
    'Ամեն ինչ միաժամանակ, ամբողջ արագությամբ։ Տախտակը խառնվում է վերջին քայլից գրեթե անմիջապես հետո։',
  htTouchTitle: 'Հպէկրանը',
  htTouchBody:
    'Հպեք բացված սալիկին՝ նրա տառը վերցնելու համար։ Հպեք վերցրած տառին՝ վերադարձնելու համար։ Ավարտել և Մաքրել տախտակի տակ են։',

  accountTitle: 'Ձեր հաշիվը',
  menuProfile: 'Իմ պրոֆիլը',
  menuGames: 'Իմ խաղերը',
  menuPublicPage: 'Իմ հանրային էջը',
  menuSignedInAs: 'Մուտք եք գործել որպես',
  signIn: 'Մուտք',
  signOut: 'Ելք',
  signInTitle: 'Մուտք գործեք կամ գրանցվեք',
  signInLead: 'Հաշիվը պահում է ձեր խաղերը՝ բոլոր սարքերում։',
  signInKeepGame: 'Մուտք գործեք, որ այս խաղը մնա, և բոլոր հաջորդները։',
  continueWithApple: 'Շարունակել Apple-ով',
  continueWithGoogle: 'Շարունակել Google-ով',
  signInOr: 'կամ',
  emailLabel: 'Էլ. փոստ',
  codeLabel: 'Կոդ',
  emailMeACode: 'Ուղարկեք ինձ կոդ',
  working: 'Մի պահ…',
  sendNewCode: 'Ուղարկել նոր կոդ',
  codeSent: 'Նայեք ձեր փոստը։ Կոդը գործում է 10 րոպե։',
  notNow: 'Ոչ հիմա',
  badEmail: 'Սա էլ. փոստի հասցե չի թվում։',
  badCode: 'Այդ կոդը չաշխատեց։ Կոդերը լրանում են 10 րոպեից և գործում են մեկ անգամ։',
  serverBusy: 'Չհաջողվեց կապվել սերվերի հետ։ Փորձեք մի պահից։',
  serverDown: 'Չհաջողվեց կապվել սերվերի հետ։',
  offline: 'Անցանց',
  leaderboardTitle: 'Առաջատարների ցանկ',
  leaderboardEmpty: 'Այս ցանկում դեռ խաղեր չկան։ Եղիր առաջինը։',
  leaderboardWouldBe: 'Որտեղ կլիներ այս խաղը',
  leaderboardThisGame: 'Այս խաղը',
  leaderboardRanksHere: 'Որտեղ է այս խաղը',
  leaderboardOutside: 'Առաջին հինգից դուրս։',
  tutAccountTitle: 'Պահիր քո միավորները',
  tutAccountBody: 'Հաշիվը պահում է քո խաղերը, դնում է միավորներդ ցանկում և պահում է քո պրոֆիլը։',
  ssoCancelled: 'Մուտքը չեղարկվեց։',
  ssoExpired: 'Չափազանց երկար տևեց։ Նորից մուտք գործեք։',
  ssoNoUsername: 'Չհաջողվեց ավարտել հաշվի ստեղծումը։ Փորձեք մի պահից։',
  ssoFailed: 'Չստացվեց։ Փորձեք նորից կամ օգտագործեք ձեր փոստը։',
  tabProfile: 'Պրոֆիլ',
  countryAny: 'Չեմ ասի',
  findCountry: 'Երկրների որոնում',
  usernameLabel: 'Օգտանուն',
  bioLabel: 'Իմ մասին',
  countryLabel: 'Երկիր',
  bioHint: 'Առանց հղումների։ {left}/{max}',
  save: 'Պահել',
  saving: 'Պահվում է…',
  saved: 'Պահված է։',
  nameTaken: 'Այդ անունը արդեն զբաղված է։',
  nameTooShort: 'Մի փոքր ավելի երկար, խնդրում ենք։',
  nameTooLong: 'Չափազանց երկար է։',
  nameBadCharacters: 'Միայն տառեր, թվեր, գծիկներ և ընդգծումներ։',
  nameBadEdges: 'Պետք է սկսվի և ավարտվի տառով կամ թվով։',
  nameMixedScripts: 'Ընտրեք մեկ այբուբեն և մնացեք դրանում։',
  nameReserved: 'Սա նման է անունների, որ մենք ենք տալիս։ Ընտրեք ուրիշը։',
  nameUnusable: 'Այդ անունը հնարավոր չէ օգտագործել։',
  bioTooLong: 'Այստեղ տեղավորվում է առավելագույնը {max} նիշ։',
  bioHasLink: 'Այստեղ հղումներ չեն թույլատրվում։',
  bioHasControl: 'Պարունակում է նիշեր, որ այստեղ չեն կարող լինել։',
  bioUnusable: 'Այդ տեքստը հնարավոր չէ օգտագործել։',
  gamesHeading: 'Խաղեր',
  gamesEmpty: 'Այստեղ դեռ ոչինչ չկա։ Մուտք գործած խաղացած խաղերը կհայտնվեն այստեղ։',
  gamesEmptyShort: 'Այստեղ դեռ ոչինչ չկա։',
  gamesLoading: 'Կարդում ենք ձեր խաղերը…',
  theirGamesLoading: 'Կարդում ենք նրանց խաղերը…',
  profileLoading: 'Կարդում ենք պրոֆիլը…',
  playerNotFound: 'Այստեղ այդ անունով ոչ ոք չկա։',
  columnWhen: 'Երբ',
  columnRounds: 'փուլ',
  columnGame: 'Խաղ',
  gameLoading: 'Կարդում ենք խաղը…',
  gameUnreadable: 'Այս խաղը չհաջողվեց կարդալ։',
  gameNotRanked: 'Խաղացվել է փոփոխված կանոններով, ուստի դասակարգված չէ։',
  detailNotKept: 'Այս խաղի բառերն ու տախտակները այլևս չեն պահվում։',
  openingBoard: 'Սկզբնական տախտակը',
  roundByRound: 'Փուլ առ փուլ',
  roundNothing: 'ոչինչ չգտնվեց',
  changeReplaced: '{letter}-ը փոխարինվեց {slot} վանդակում',
  changeWildOn: '{letter} ջոկեր {slot} վանդակում',
  changeWildOff: '{letter} ջոկերը հեռացավ {slot} վանդակից',
  keepThisGame: 'Պահել այս խաղը',
  savedToYourGames: 'Պահվեց ձեր խաղերում։',

  reportThis: 'Բողոքել',
  reportHeading: 'Բողոքել սրանից',
  reportTheName: 'Անունը',
  reportTheBio: 'Ներկայացումը',
  reportTheScore: 'Միավորը',
  reportWhy: 'Ի՞նչն է սխալ։ Ըստ ցանկության։',
  reportSend: 'Ուղարկել բողոքը',
  reportSent: 'Շնորհակալություն։ Ինչ-որ մեկը կնայի։',
  reportSignIn: 'Մտեք՝ որևէ բան հայտնելու համար։',
  reportGone: 'Դա այլևս չկա։',
  reportCancel: 'Չեղարկել',

  deleteAccount: 'Ջնջել իմ հաշիվը',
  deleteAccountWhat: 'Սա ընդմիշտ հեռացնում է ձեր էջը և պահած բոլոր խաղերը։ Հետդարձ չկա։',
  deleteAccountAsk: 'Ուղարկեք ինձ կոդ',
  deleteConfirm: 'Ջնջել ընդմիշտ',
  deleteGone: 'Ձեր հաշիվը ջնջված է։ Շնորհակալություն խաղի համար։',
  deleteNoAddress:
    'Այս հաշվին հաստատված հասցե չկա, ուստի ջնջումը հնարավոր չէ ստուգել։ Կապվեք մեզ հետ, և կանենք ձեռքով։',

  rulesetFacts: 'Բառեր՝ {letters} · ամեն {seconds} մեկ նոր սալիկ',
  plurals: {
    words: { one: '{n} բառ', other: '{n} բառ' },
    rounds: { one: '{n} փուլ', other: '{n} փուլ' },
    flips: { one: '{n} քայլ', other: '{n} քայլ' },
    ticks: { one: '{n} տակտ', other: '{n} տակտ' },
    points: { one: '{n} միավոր', other: '{n} միավոր' },
    minLetters: { one: 'առնվազն {n} տառից', other: 'առնվազն {n} տառից' },
    seconds: { one: '{n} վայրկյանը', other: '{n} վայրկյանը' },
  },
}
